/**
 * MASTER B2B SCRIPT (DEMO-PROOF / PRODUCTION FRIENDLY)
 * POST /apps/inventory  { action: "..." }
 *
 * Modes:
 *  - Agent mode: localStorage agent_active_location_id + agent_active_stock_code
 *  - Customer mode: window.customerShipmentSite
 *  - Retail: preserve normal Shopify theme behavior
 *
 * Key fixes:
 *  - In-flight guard to stop flicker/racing requests
 *  - Price patch uses .b2b-price-display (added in card template)
 *  - ✅ Adds ONE global delegated AJAX Add-to-Cart handler (fixes “click does nothing”)
 *  - ✅ Respects btn.dataset.busy so inventory checks don’t fight “Adding…”
 */

(function () {
  const PROXY_PATH = "/apps/inventory";
  const APPROVED_SITES = new Set(["41010", "41012"]);
  const WAREHOUSE_AVAILABILITY_PARAM = "filter.b2b.availability";

  // ---------- helpers ----------
  const postJSON = async (url, payload) => {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-store",
        "X-B2B-Context-Key": contextKey(),
      },
      body: JSON.stringify(payload),
    });

    const text = await res.text().catch(() => "");
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      if ((text || "").trim().startsWith("<")) {
        const err = new Error(
          "Proxy returned HTML (likely login redirect or theme rendered). Ensure App Proxy is configured and /apps/inventory is allowed."
        );
        err.status = res.status;
        err.raw = text.slice(0, 400);
        throw err;
      }
    }

    if (!res.ok) {
      const msg = data?.error || `Request failed (${res.status})`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const uniq = (arr) => [...new Set((arr || []).filter(Boolean))];

  const normalizeGid = (maybeGidOrNumber, resourceName) => {
    if (!maybeGidOrNumber) return "";
    const s = String(maybeGidOrNumber).trim();
    if (!s) return "";
    if (s.startsWith("gid://")) return s;
    const digits = s.match(/\d+$/)?.[0];
    if (!digits) return "";
    return `gid://shopify/${resourceName}/${digits}`;
  };

  const normalizeStockCode = (v) => {
    const site = String(v || "").trim();
    return APPROVED_SITES.has(site) ? site : "";
  };

  const contextKey = () => {
    const server = window.b2bServerContext || {};
    const agentLoc = localStorage.getItem("agent_active_location_id") || "";
    const agentSite = normalizeStockCode(localStorage.getItem("agent_active_stock_code"));
    return `${server.mode || "retail"}:${server.companyLocationId || agentLoc}:${server.shipmentSite || agentSite}`;
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

  const moneyFmt = (amount, currencyCode) => {
    const n = Number(amount);
    if (!isFinite(n)) return "";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode || "USD",
      }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function fetchWithTimeout(url, options, timeoutMs = 15000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  const getCells = () => document.querySelectorAll(".b2b-inventory-cell");
  const getPdpForms = () =>
    document.querySelectorAll("product-form.product-form[data-b2b-product-handle]");

  const getVariantIdFromForm = (form) => {
    const input = form.querySelector('input[name="id"]');
    return String(input?.value || "").trim();
  };

  const safeText = (v, max = 40) => {
    const s = String(v || "").trim().replace(/\s+/g, " ");
    if (!s) return "";
    return s.length > max ? s.slice(0, max) : s;
  };

  const readAgentIdentity = () => {
    const firstName =
      safeText(window.agentFirstName, 40) ||
      safeText(window.customerFirstName, 40) ||
      safeText(localStorage.getItem("agent_first_name"), 40) ||
      "";

    const lastName =
      safeText(window.agentLastName, 40) ||
      safeText(window.customerLastName, 40) ||
      safeText(localStorage.getItem("agent_last_name"), 40) ||
      "";

    return { firstName, lastName };
  };

  // ---------- context ----------
  const readAgentContext = () => {
    const rawLoc = localStorage.getItem("agent_active_location_id");
    const rawCode = localStorage.getItem("agent_active_stock_code");
    const rawEmail = window.agentEmail || localStorage.getItem("agent_email") || "";

    const locationId = normalizeGid(rawLoc, "CompanyLocation");
    const stockCode = normalizeStockCode(rawCode);
    const agentEmail = String(rawEmail || "").trim();

    const isAgentMode = !!locationId && !!stockCode;
    return { isAgentMode, locationId, stockCode, agentEmail };
  };

  const readCustomerContext = () => {
    const server = window.b2bServerContext || {};
    const shipmentSite = normalizeStockCode(server.shipmentSite);
    return {
      isCustomerMode: server.mode === "customer",
      shipmentSite,
      companyLocationId: String(server.companyLocationId || "").trim(),
    };
  };

  // ---------- UI ----------
  function setBtnState(cell, enabled, label) {
    const btn = cell.querySelector('button[name="add"]');
    if (!btn) return;

    // If the button is currently in an "Adding..." state, don’t fight it.
    if (btn.dataset.busy === "1") return;

    btn.disabled = !enabled;
    btn.innerText = label || (enabled ? "Add to Cart" : "Out of Stock");
    btn.classList.toggle("disabled", !enabled);
    btn.classList.remove("loading");
    cell.closest("tr, .product-list-item")?.setAttribute(
      "data-b2b-availability",
      enabled ? "available" : "out-of-stock"
    );
  }

  function lockAllButtonsLoading() {
    document
      .querySelectorAll('.b2b-inventory-cell button[name="add"]')
      .forEach((btn) => {
        if (btn.dataset.busy === "1") return;
        btn.disabled = true;
        btn.innerText = "Loading Stock...";
        btn.classList.add("loading");
        btn.classList.remove("disabled");
      });
  }

  function setPdpBtnState(form, enabled) {
    const btn = form.querySelector('button[name="add"].product-form__submit');
    if (!btn) return;

    if (btn.dataset.busy === "1" || btn.classList.contains("loading")) return;

    const addLabel = btn.dataset.b2bAddLabel || "Add to Cart";
    const soldOutLabel = btn.dataset.b2bOosLabel || "Out of Stock";
    const labelEl = btn.querySelector("span");

    btn.disabled = !enabled;
    btn.classList.toggle("disabled", !enabled);
    if (labelEl) labelEl.textContent = enabled ? addLabel : soldOutLabel;

    // Re-enable the hidden variant ID input so FormData includes it when the
    // button is re-enabled. buy-buttons.liquid renders it disabled when
    // variant.available == false; without this the variant ID is silently
    // dropped from form submissions.
    const idInput = form.querySelector('input[name="id"].product-variant-id');
    if (idInput) idInput.disabled = !enabled;
  }

  function unlockAllButtons() {
    document
      .querySelectorAll('.b2b-inventory-cell button[name="add"]')
      .forEach((btn) => {
        if (btn.dataset.busy === "1") return;
        btn.disabled = false;
        btn.innerText = "Add to Cart";
        btn.classList.remove("disabled", "loading");
      });
  }

  function failClosed() {
    runToken++;
    document.querySelectorAll('.b2b-inventory-cell button[name="add"]').forEach((btn) => {
      if (btn.dataset.busy === "1") return;
      btn.disabled = true;
      btn.innerText = "Unavailable";
      btn.classList.add("disabled");
      btn.classList.remove("loading");
    });
    getPdpForms().forEach((form) => setPdpBtnState(form, false));
  }

  function getAssignedQuantity(variant, assignedSite) {
    const levels = variant?.inventoryItem?.inventoryLevels?.edges;
    if (Array.isArray(levels)) {
      const matchingLevel = levels.find((edge) => {
        const node = edge?.node || {};
        const code = String(
          node.stockSourceCode || node.location?.stockSourceCode || node.location?.name || ""
        ).trim();
        return code === assignedSite;
      });
      const quantities = matchingLevel?.node?.quantities || [];
      const quantity =
        quantities.find((q) => q.name === "available")?.quantity ??
        quantities[0]?.quantity;
      return Number.isFinite(Number(quantity)) ? Number(quantity) : 0;
    }

    const quantity = variant?.inventoryItem?.inventoryLevel?.quantities?.[0]?.quantity;
    return Number.isFinite(Number(quantity)) ? Number(quantity) : 0;
  }

  function sortAvailableFirst(cells) {
    const groups = new Map();
    Array.from(cells).forEach((cell) => {
      const item = cell.closest("tr, .product-list-item") || cell.parentElement;
      const container = item?.parentElement;
      if (!item || !container) return;
      if (!groups.has(container)) groups.set(container, []);
      groups.get(container).push(item);
    });
    groups.forEach((items, container) => {
      items
        .sort((a, b) => {
          const aAvailable = a.dataset.b2bAvailability === "available";
          const bAvailable = b.dataset.b2bAvailability === "available";
          return Number(bAvailable) - Number(aAvailable);
        })
        .forEach((item) => container.appendChild(item));
    });
  }

  function getWarehouseAvailabilitySelection() {
    const params = new URLSearchParams(window.location.search);
    return new Set(params.getAll(WAREHOUSE_AVAILABILITY_PARAM));
  }

  function applyWarehouseAvailabilityFilter(cells) {
    const selection = getWarehouseAvailabilitySelection();
    if (selection.size !== 1) return;

    const requiredAvailability = selection.has("1") ? "available" : "out-of-stock";

    const rows = new Set();
    Array.from(cells).forEach((cell) => {
      const row = cell.closest("tr, .product-list-item");
      if (row) rows.add(row);
    });

    let visibleCount = 0;
    rows.forEach((row) => {
      const visible = row.dataset.b2bAvailability === requiredAvailability;
      if (visible) visibleCount += 1;
      else row.remove();
    });

    const noResults = document.querySelector(".no-results-row");
    if (noResults) noResults.style.display = visibleCount ? "none" : "table-row";
  }

  function updateWarehouseProductCount() {
    const rows = [...document.querySelectorAll("#product-grid tbody tr.product-card")];
    const count = rows.length;
    const availableCount = rows.filter(
      (row) => row.dataset.b2bAvailability === "available"
    ).length;
    const outOfStockCount = count - availableCount;
    const selection = getWarehouseAvailabilitySelection();
    const displayedCount = selection.size === 1
      ? (selection.has("1") ? availableCount : outOfStockCount)
      : count;
    document.querySelectorAll("#ProductCountDesktop, #ProductCount").forEach((element) => {
      element.textContent = `${displayedCount} products`;
    });

    document.querySelectorAll(`input[name="${WAREHOUSE_AVAILABILITY_PARAM}"]`).forEach((input) => {
      input.checked = selection.has(input.value);
      const label = input.closest("label");
      if (!label) return;
      const value = input.value === "1" ? availableCount : outOfStockCount;
      const text = label.querySelector(".facet-checkbox__text");
      if (text) {
        Array.from(text.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .forEach((node) => {
            node.textContent = node.textContent.replace(/\(\d+\)/, `(${value})`);
          });
      }
      const hiddenText = label.querySelector(".visually-hidden");
      if (hiddenText) hiddenText.textContent = hiddenText.textContent.replace(/\d+/, String(value));
    });
  }

  function paginateWarehouseSortedRows(cells) {
    if (!window.__b2bWarehouseAllPagesLoaded) return;

    const rows = [...document.querySelectorAll("#product-grid tbody tr.product-card")];
    if (!rows.length) {
      Array.from(cells).forEach((cell) => {
        const row = cell.closest("tr, .product-list-item");
        if (row?.isConnected && !rows.includes(row)) rows.push(row);
      });
    }

    const pageSize = Number(window.__b2bWarehousePageSize) || rows.length;
    const page = Math.max(1, Number(new URLSearchParams(window.location.search).get("page")) || 1);
    const firstVisible = (page - 1) * pageSize;
    const lastVisible = firstVisible + pageSize;

    rows.forEach((row, index) => {
      row.hidden = index < firstVisible || index >= lastVisible;
    });

    const pagination = document.querySelector(".pagination");
    if (!pagination) return;
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const baseParams = new URLSearchParams(window.location.search);
    baseParams.delete("page");
    pagination.innerHTML = pageCount > 1
      ? `<ul class="pagination__list list-unstyled">${Array.from({ length: pageCount }, (_, index) => {
          const pageNumber = index + 1;
          const pageParams = new URLSearchParams(baseParams);
          pageParams.set("page", String(pageNumber));
          const current = pageNumber === page ? ' aria-current="page"' : "";
          return `<li><a class="pagination__item link" href="${window.location.pathname}?${pageParams}"${current}>${pageNumber}</a></li>`;
        }).join("")}</ul>`
      : "";
  }

  async function loadAllWarehouseFilteredPages(loadAllB2BPages = false) {
    const params = new URLSearchParams(window.location.search);
    if (!loadAllB2BPages && !params.getAll("filter.v.availability").includes("1")) return false;
    if (window.__b2bWarehouseFilteredPagesKey === window.location.search) return false;

    const grid = document.querySelector("#ProductGridContainer");
    const table = grid?.querySelector("#product-grid");
    const tbody = table?.querySelector("tbody");
    if (!tbody) return false;
    const currentRows = tbody.querySelectorAll("tr.product-card");
    window.__b2bWarehousePageSize = currentRows.length || window.__b2bWarehousePageSize;

    const pageLinks = [...document.querySelectorAll(".pagination a")]
      .map((link) => link.href ? Number(new URL(link.href).searchParams.get("page")) : NaN)
      .filter(Number.isFinite);
    const lastPage = Math.max(1, ...pageLinks);
    if (lastPage <= 1) {
      window.__b2bWarehouseFilteredPagesKey = window.location.search;
      return false;
    }

    window.__b2bWarehouseFilteredPagesKey = window.location.search;
    const sectionId = table.dataset.id;
    const pageRequests = Array.from({ length: lastPage }, (_, index) => {
      const pageParams = new URLSearchParams(params);
      pageParams.set("page", String(index + 1));
      if (sectionId) pageParams.set("section_id", sectionId);
      return fetch(`${window.location.pathname}?${pageParams}`, {
        credentials: "same-origin",
        cache: "no-store",
      }).then((response) => {
        if (!response.ok) throw new Error(`Page ${index + 1} request failed (${response.status})`);
        return response.text();
      });
    });

    try {
      const pages = await Promise.all(pageRequests);
      const rows = pages.flatMap((html) => {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        return [...parsed.querySelectorAll("#product-grid tbody tr.product-card")];
      });
      const noResults = tbody.querySelector(".no-results-row");
      tbody.replaceChildren(...rows, ...(noResults ? [noResults] : []));
      window.__b2bWarehouseAllPagesLoaded = true;
      return true;
    } catch (error) {
      console.error("[B2B] Failed to load all warehouse-filtered pages:", error);
      window.__b2bWarehouseFilteredPagesKey = "";
      window.__b2bWarehouseAllPagesLoaded = false;
      return false;
    }
  }

  // ---------- in-flight guard (prevents flicker) ----------
  let runToken = 0;
  let lastContextKey = "";

  function clearInventoryState() {
    runToken++;
    document.querySelectorAll(".b2b-inventory-cell").forEach((cell) => {
      const btn = cell.querySelector('button[name="add"]');
      if (btn && btn.dataset.busy !== "1") {
        btn.disabled = true;
        btn.innerText = "Loading Stock...";
        btn.classList.remove("disabled");
        btn.classList.add("loading");
      }
      const stock = cell.querySelector(".b2b-stock-info");
      if (stock) stock.textContent = "";
    });
  }

  // ---------- CUSTOMER MODE ----------
  async function runCustomerLogic(shipmentSite) {
    const myToken = ++runToken;
    const cells = getCells();
    const pdpForms = getPdpForms();
    if (!cells.length && !pdpForms.length) return;

    const handles = uniq([
      ...Array.from(cells).map((c) => c.dataset.handle),
      ...Array.from(pdpForms).map((f) => f.dataset.b2bProductHandle),
    ]);
    if (!handles.length) return;

    if (cells.length) lockAllButtonsLoading();

    try {
      const map = new Map();
      const BATCH_SIZE = 50;
      for (let i = 0; i < handles.length; i += BATCH_SIZE) {
        const data = await postJSON(PROXY_PATH, {
          action: "inventory",
          handles: handles.slice(i, i + BATCH_SIZE),
          stockSourceCode: shipmentSite,
          companyLocationId: String(window.b2bServerContext?.companyLocationId || "").trim(),
        });

        if (myToken !== runToken) return;
        data?.data?.products?.edges?.forEach((e) => map.set(e.node.handle, e.node));
      }

      if (myToken !== runToken) return; // stale response

      cells.forEach((cell) => {
        const handle = cell.dataset.handle;
        const variantId = String(cell.dataset.variantId || "");
        const productNode = map.get(handle);

        let availableQty = 0;

        if (productNode?.variants?.edges?.length) {
          const variantEdge = productNode.variants.edges.find((v) =>
            String(v.node.id).endsWith(`/${variantId}`)
          );
          const variant = variantEdge?.node;

          availableQty = getAssignedQuantity(variant, shipmentSite);
        }

        setBtnState(
          cell,
          availableQty > 0,
          availableQty > 0 ? "Add to Cart" : "Out of Stock"
        );
      });

      pdpForms.forEach((form) => {
        const handle = String(form.dataset.b2bProductHandle || "").trim();
        const variantId = getVariantIdFromForm(form);
        if (!handle || !variantId) return;

        const productNode = map.get(handle);
        const variantEdge = productNode?.variants?.edges?.find((v) =>
          String(v.node.id).endsWith(`/${variantId}`)
        );
        const qty = getAssignedQuantity(variantEdge?.node, shipmentSite);

        setPdpBtnState(form, qty > 0);
      });
      sortAvailableFirst(cells);
      updateWarehouseProductCount();
      applyWarehouseAvailabilityFilter(cells);
      paginateWarehouseSortedRows(cells);
    } catch (err) {
      console.error("[B2B] Customer inventory failed:", err);
      failClosed();
    }
  }

  // ---------- AGENT MODE ----------
  async function runAgentLogic(locationId, stockCode) {
    const myToken = ++runToken;

    const cells = getCells();
    const pdpForms = getPdpForms();

    // Cart page has no cells — hijack must still work.
    setupCheckoutHijack();

    if (!cells.length && !pdpForms.length) return;

    const handles = uniq([
      ...Array.from(cells).map((c) => c.dataset.handle),
      ...Array.from(pdpForms).map((f) => f.dataset.b2bProductHandle),
    ]);
    if (!handles.length) return;

    if (cells.length) lockAllButtonsLoading();

    const BATCH_SIZE = 50;
    for (let i = 0; i < handles.length; i += BATCH_SIZE) {
      const batchHandles = handles.slice(i, i + BATCH_SIZE);

      try {
        const data = await postJSON(PROXY_PATH, {
          action: "get-b2b-context",
          handles: batchHandles,
          companyLocationId: locationId,
          stockSourceCode: stockCode,
        });

        if (myToken !== runToken) return; // stale run

        const map = new Map();
        if (data?.data?.products?.edges) {
          data.data.products.edges.forEach((e) => map.set(e.node.handle, e.node));
        }

        cells.forEach((cell) => {
          const handle = cell.dataset.handle;
          if (!batchHandles.includes(handle)) return;

          const variantId = String(cell.dataset.variantId || "");
          const productNode = map.get(handle);

          if (!productNode) {
            setBtnState(cell, false, "Out of Stock");
            return;
          }

          const variantEdge = productNode.variants.edges.find((v) =>
            String(v.node.id).endsWith(`/${variantId}`)
          );
          const variant = variantEdge?.node;

          if (!variant) {
            setBtnState(cell, false, "Out of Stock");
            return;
          }

          // Price patch
          const p = variant.contextualPricing?.price;
          if (p?.amount && p?.currencyCode) {
            const priceEl =
              cell.closest("tr")?.querySelector(".b2b-price-display") ||
              cell.querySelector(".b2b-price-display");

            if (priceEl) {
              priceEl.innerText = moneyFmt(p.amount, p.currencyCode);
              priceEl.classList.add("b2b-patched");
            }
          }

          const qty = getAssignedQuantity(variant, stockCode);

          setBtnState(cell, qty > 0, qty > 0 ? "Add to Cart" : "Out of Stock");
        });

        pdpForms.forEach((form) => {
          const handle = String(form.dataset.b2bProductHandle || "").trim();
          if (!batchHandles.includes(handle)) return;

          const variantId = getVariantIdFromForm(form);
          if (!variantId) return;

          const productNode = map.get(handle);
          const variantEdge = productNode?.variants?.edges?.find((v) =>
            String(v.node.id).endsWith(`/${variantId}`)
          );
          const safeQty = getAssignedQuantity(variantEdge?.node, stockCode);

          setPdpBtnState(form, safeQty > 0);
        });
      } catch (err) {
        console.error("[B2B] Agent batch failed:", err);
        cells.forEach((cell) => {
          if (batchHandles.includes(cell.dataset.handle)) {
            setBtnState(cell, false, "Out of Stock");
          }
        });
        pdpForms.forEach((form) => {
          const handle = String(form.dataset.b2bProductHandle || "").trim();
          if (batchHandles.includes(handle)) setPdpBtnState(form, false);
        });
      }
    }
    sortAvailableFirst(cells);
    updateWarehouseProductCount();
    applyWarehouseAvailabilityFilter(cells);
    paginateWarehouseSortedRows(cells);
  }

  function setupVariantChangeHooks() {
    if (window.__b2bVariantHooksBound) return;
    window.__b2bVariantHooksBound = true;

    const rerun = () => {
      window.setTimeout(() => {
        if (typeof window.runB2BInventoryCheck === "function") {
          window.runB2BInventoryCheck();
        }
      }, 80);
    };

    document.addEventListener(
      "change",
      (e) => {
        const form = e.target?.closest?.(
          "product-form.product-form[data-b2b-product-handle]"
        );
        if (!form) return;
        rerun();
      },
      true
    );

    document.addEventListener("variant:change", rerun, true);
  }

  // ---------- Checkout hijack (agent mode only) ----------
  function setupCheckoutHijack() {
    const agentNow = readAgentContext();
    if (!agentNow.isAgentMode) return;

    const selectors =
      'button[name="checkout"], .cart__checkout-button, [href="/checkout"], form[action="/cart"] [type="submit"]';
    const checkoutBtns = document.querySelectorAll(selectors);
    if (!checkoutBtns.length) return;

    checkoutBtns.forEach((btn) => {
      if (btn.dataset.hijacked === "true") return;

      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);

      newBtn.dataset.hijacked = "true";
      newBtn.classList.add("agent-checkout-active");

      newBtn.innerText = "Place B2B Order";
      newBtn.style.backgroundColor = "#008060";
      newBtn.style.fontSize = "13px";

      newBtn.addEventListener("click", async (e) => {
        const agent = readAgentContext();
        e.preventDefault();
        e.stopPropagation();

        if (!agent.isAgentMode) {
          window.location.href = "/checkout";
          return;
        }

        newBtn.innerText = "Processing...";
        newBtn.disabled = true;

        try {
          if (!isValidEmail(agent.agentEmail)) {
            throw new Error(
              "Missing agent email. Ensure your portal sets localStorage agent_email."
            );
          }

          const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) =>
            r.json()
          );
          if (!cart || cart.item_count === 0) throw new Error("Cart is empty");

          // Only trust the cached name/address if they were tagged for the
          // currently active company location. localStorage is shared across
          // all tabs of this origin, so a stale/other tab's cache must never
          // be sent for this order (that mismatch caused wrong bill-to/rep
          // names to appear against the wrong customer).
          const cachedContextLoc = localStorage.getItem("agent_company_context_loc") || "";
          const cacheMatchesActiveLocation =
            !!cachedContextLoc && cachedContextLoc === String(agent.locationId || "");

          let clientName = cacheMatchesActiveLocation
            ? localStorage.getItem("agent_company_name") || ""
            : "";
          let clientAddr = null;
          if (cacheMatchesActiveLocation) {
            try {
              clientAddr = JSON.parse(
                localStorage.getItem("agent_company_address") || "null"
              );
            } catch {}
          }

          if (!clientName || !clientAddr) {
            newBtn.innerText = "Fetching Client...";
            const companyData = await postJSON(PROXY_PATH, {
              action: "get-agent-companies",
              agentEmail: agent.agentEmail,
            });

            const norm = (v) => String(v || "").trim();
            const targetLoc = norm(agent.locationId);

            const match = (companyData.companies || []).find(
              (c) => norm(c.locationId) === targetLoc
            );

            if (!match) {
              throw new Error("Could not find company details. Please re-select client.");
            }

            clientName = match.companyName || match.displayName || match.name || "";
            clientAddr = match.address || {};

            localStorage.setItem("agent_company_name", clientName);
            localStorage.setItem("agent_company_address", JSON.stringify(clientAddr));
            localStorage.setItem("agent_company_context_loc", targetLoc);
          }

          const agentDetails = readAgentIdentity();

          newBtn.innerText = "Generating Invoice...";

          const data = await postJSON(PROXY_PATH, {
            action: "create-draft-order",
            companyLocationId: agent.locationId,
            items: cart.items,
            agentEmail: agent.agentEmail,
            agentDetails,
            clientDetails: {
              name: clientName,
              address: clientAddr,
              stockCode: agent.stockCode,
            },
          });

          if (data?.invoiceUrl) {
            window.location.href = data.invoiceUrl;
            return;
          }

          throw new Error(data?.error || "Order Creation Failed");
        } catch (err) {
          console.error("Order Failed:", err);
          alert("Order Failed: " + (err.message || "Unknown error"));
          newBtn.disabled = false;
          newBtn.innerText = "Place B2B Order";
        }
      });
    });
  }

  // ---------- Add to cart (single delegated handler) ----------
  function setupAddToCartHandler() {
    if (window.__b2bAddToCartBound) return;
    window.__b2bAddToCartBound = true;

    // Serialize storefront cart writes to avoid 429 bursts when users click
    // multiple rows quickly.
    let cartAddQueue = Promise.resolve();

    document.addEventListener(
      "click",
      async (e) => {
        const btn = e.target.closest(".add-to-cart-ajax");
        if (!btn) return;

        // Intercept early + prevent other theme scripts from swallowing it
        e.preventDefault();
        e.stopPropagation();

        if (btn.disabled) return;

        const productId = btn.getAttribute("data-product-id");
        if (!productId) return;

        const form = document.getElementById(`card-product-form-${productId}`);
        if (!form) {
          console.warn("[B2B] Missing form for product:", productId);
          return;
        }

        const idInput = form.querySelector('input[name="id"]');
        const qtyInput =
          form.querySelector('input[name="quantity"]') ||
          document.getElementById(`Quantity-${productId}`);

        const variantId = idInput ? String(idInput.value || "").trim() : "";
        const quantity = qtyInput ? Number(qtyInput.value || 1) : 1;

        if (!variantId || !Number.isFinite(quantity) || quantity <= 0) return;

        // UI lock (and stop inventory check from fighting this button)
        btn.dataset.busy = "1";
        const originalLabel = btn.innerText || "Add to Cart";
        btn.disabled = true;
        btn.innerText = "Adding...";

        const msgBox = document.getElementById(`add-message-${productId}`);
        if (msgBox) msgBox.style.display = "none";

        const runAdd = async () => {
          const MAX_CART_ADD_ATTEMPTS = 3;
          let res = null;
          let data = {};

          for (let attempt = 1; attempt <= MAX_CART_ADD_ATTEMPTS; attempt++) {
            if (attempt > 1) btn.innerText = "Retrying...";

            res = await fetchWithTimeout(
              `${window.Shopify.routes.root}cart/add.js`,
              {
                method: "POST",
                credentials: "same-origin",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({
                  items: [{ id: variantId, quantity }],
                }),
              },
              15000
            );

            data = await res.json().catch(() => ({}));

            if (res.status !== 429) break;

            if (attempt < MAX_CART_ADD_ATTEMPTS) {
              const retryAfterSec = Number(res.headers.get("Retry-After") || 0);
              const retryMs =
                Number.isFinite(retryAfterSec) && retryAfterSec > 0
                  ? retryAfterSec * 1000
                  : Math.min(4000, 500 * Math.pow(2, attempt - 1));

              console.warn(
                `[B2B] cart/add.js throttled (429). Retrying in ${retryMs}ms (attempt ${attempt}/${MAX_CART_ADD_ATTEMPTS}).`
              );
              await wait(retryMs);
            }
          }

          if (!res.ok) {
            let message =
              data?.description ||
              data?.message ||
              data?.error ||
              `Could not add item (HTTP ${res.status})`;

            if (res.status === 429) {
              message =
                "Shopify is temporarily rate-limiting add-to-cart requests. Please wait a few seconds and try again.";
            }

            if (res.status === 422) {
              console.warn(
                "[B2B] cart/add.js returned 422. B2B warehouse shows stock but " +
                "Shopify storefront location may have 0 inventory. " +
                "Fix: Shopify Admin → Locations → assign the correct warehouse to the Online Store sales channel."
              );
              message =
                "Unable to add to cart. Your warehouse has stock but Shopify's storefront location shows sold out. " +
                "Please contact your administrator.";
            }

            throw new Error(message);
          }

          btn.innerText = "Added!";

          try {
            const cart = await fetchWithTimeout(
              `${window.Shopify.routes.root}cart.js`,
              { credentials: "same-origin" },
              10000
            ).then((r) => r.json());

            document
              .querySelectorAll('.cart-count-bubble span[aria-hidden="true"]')
              .forEach((el) => (el.textContent = cart.item_count));
          } catch (_) {}
        };

        try {
          btn.innerText = "Queued...";
          const task = cartAddQueue.catch(() => {}).then(async () => {
            btn.innerText = "Adding...";
            await runAdd();
          });

          cartAddQueue = task;
          await task;

          setTimeout(() => {
            btn.dataset.busy = "0";
            btn.disabled = false;
            btn.innerText = originalLabel;
          }, 1200);
        } catch (err) {
          console.error("[B2B] Add to cart failed:", err);

          if (msgBox) {
            msgBox.textContent = err.message || "Could not add item.";
            msgBox.style.display = "block";
          }

          btn.dataset.busy = "0";
          btn.disabled = false;
          btn.innerText = originalLabel;
        }
      },
      true // capture: intercept before other theme handlers
    );
  }

  function observeCartRerenders() {
    try {
      const mo = new MutationObserver(() => {
        setupCheckoutHijack();
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (_) {}
  }

  // ---------- entrypoint ----------
  function runB2BInventoryCheck() {
    const nextContextKey = contextKey();
    if (lastContextKey && lastContextKey !== nextContextKey) clearInventoryState();
    lastContextKey = nextContextKey;

    const agent = readAgentContext();
    const customer = readCustomerContext();

    loadAllWarehouseFilteredPages(agent.isAgentMode || customer.isCustomerMode).then((changed) => {
      if (changed && typeof window.runB2BInventoryCheck === "function") {
        window.runB2BInventoryCheck();
      }
    });

    if (agent.isAgentMode) setupCheckoutHijack();

    if (agent.isAgentMode && APPROVED_SITES.has(agent.stockCode)) {
      return runAgentLogic(agent.locationId, agent.stockCode);
    }
    if (customer.isCustomerMode && customer.shipmentSite) {
      return runCustomerLogic(customer.shipmentSite);
    }

    if (agent.isAgentMode || customer.isCustomerMode) return failClosed();
    return unlockAllButtons();
  }

  document.addEventListener("DOMContentLoaded", () => {
    window.runB2BInventoryCheck = runB2BInventoryCheck;

    // ✅ required so Add-to-Cart works (your card button is type="button")
    setupAddToCartHandler();
    setupVariantChangeHooks();

    observeCartRerenders();
    window.addEventListener("storage", (event) => {
      if (event.key?.startsWith("agent_")) runB2BInventoryCheck();
    });
    runB2BInventoryCheck();
  });
})();